import { forwardRef, useCallback, useEffect, useImperativeHandle } from "react";
import { useSharedValue } from "react-native-reanimated";
import usePlayer from "../../hooks/usePlayer";
import AudioPlayer, { type AudioPlayerType } from "../AudioPlayer";
import type { SessionAudioPlayerTrackingHandler } from "../StudySession/types";
import { useToast } from "../Toast";

export type AudioPlayerRef = {
	play: () => Promise<void>;
	pause: () => void;
	isEnded: boolean;
	seekTo: (timeMs: number) => Promise<void>;
	isPlaying: boolean;
	isLoaded: boolean;
};

type Props = {
	handleTracking?: SessionAudioPlayerTrackingHandler;
	uri: string;
	onEnd?: () => void;
	onReplay?: () => void;
	hidden?: boolean;
	autoPlay?: boolean;
	waveformWidth?: number;
	updateWaveformWidth?: (width: number) => void;
	showWaveform?: boolean;
	useReanimated?: boolean;
	onDurationLoaded?: (durationInMilliseconds: number) => void;
	type?: AudioPlayerType;
	playOnce?: boolean;
	playOnceInteractive?: boolean;
	forceCompleted?: boolean;
	disabled?: boolean;
	locked?: boolean;
	borderRadius?: number;
};

const AudioPlayerContainer = forwardRef<AudioPlayerRef, Props>(
	(
		{
			uri,
			onEnd,
			onReplay,
			hidden,
			autoPlay,
			updateWaveformWidth,
			waveformWidth,
			showWaveform = true,
			useReanimated,
			onDurationLoaded,
			handleTracking,
			type,
			playOnce,
			playOnceInteractive,
			forceCompleted,
			disabled,
			locked,
			borderRadius,
		},
		ref,
	) => {
		const {
			isPlaying,
			currentTimeInMilliseconds,
			totalTimeInMilliseconds,
			pauseSound,
			playSound,
			isBuffering,
			onSeek,
			isEnded,
			isLoaded,
			hasError,
		} = usePlayer(uri, undefined, autoPlay);

		const { showToast } = useToast();

		useImperativeHandle(
			ref,
			() => ({
				play: playSound,
				pause: pauseSound,
				isEnded,
				seekTo: onSeek,
				isPlaying,
				isLoaded,
			}),
			[playSound, pauseSound, onSeek, isEnded, isPlaying, isLoaded],
		);

		const handlePlayPause = useCallback(() => {
			if (isEnded) {
				onSeek(0);
				playSound();
				onReplay?.();
			} else if (isPlaying) {
				handleTracking?.(uri, "paused");
				pauseSound();
			} else {
				handleTracking?.(uri, "started");
				playSound();
			}
		}, [
			isEnded,
			isPlaying,
			onSeek,
			playSound,
			pauseSound,
			onReplay,
			handleTracking,
			uri,
		]);

		// biome-ignore lint/correctness/useExhaustiveDependencies: only called when isEnded changes
		useEffect(() => {
			if (isEnded) {
				handleTracking?.(uri, "ended");
				onEnd?.();
			}
		}, [isEnded]);

		useEffect(() => {
			if (!hasError) {
				return;
			}

			showToast({
				message: "An error occurred while playing the audio.",
			});
		}, [hasError, showToast]);

		// biome-ignore lint/correctness/useExhaustiveDependencies: only called when values change
		useEffect(() => {
			if (isLoaded && totalTimeInMilliseconds) {
				onDurationLoaded?.(totalTimeInMilliseconds);
			}
		}, [isLoaded, totalTimeInMilliseconds]);

		const progressShared = useSharedValue(0);
		useEffect(() => {
			progressShared.value =
				isEnded || forceCompleted
					? 1
					: totalTimeInMilliseconds
						? currentTimeInMilliseconds / totalTimeInMilliseconds
						: 0;
		}, [
			isEnded,
			forceCompleted,
			currentTimeInMilliseconds,
			totalTimeInMilliseconds,
			progressShared,
		]);

		const currentTimeInSeconds = Math.floor(currentTimeInMilliseconds / 1000);

		return (
			<AudioPlayer
				key={uri}
				uri={uri}
				currentTimeInSeconds={currentTimeInSeconds}
				totalTimeInMilliseconds={totalTimeInMilliseconds}
				progressShared={progressShared}
				isPlaying={isPlaying}
				handlePlayPause={handlePlayPause}
				isBuffering={isBuffering || !isLoaded}
				isEnded={isEnded}
				onSeek={onSeek}
				hidden={hidden}
				waveformWidth={waveformWidth}
				updateWaveformWidth={updateWaveformWidth}
				showWaveform={showWaveform}
				useReanimated={useReanimated}
				type={type}
				playOnce={playOnce}
				playOnceInteractive={playOnceInteractive}
				forceCompleted={forceCompleted}
				disabled={disabled}
				locked={locked}
				borderRadius={borderRadius}
			/>
		);
	},
);

export default AudioPlayerContainer;
